import React, {useMemo, useState, useRef, useEffect, memo} from 'react';
import {Canvas, useThree, useFrame} from '@react-three/fiber';
import {OrbitControls, Cylinder, Html} from '@react-three/drei';
import * as turf from '@turf/turf';
import {useDrillholeData} from "../components/hooks/useDrillholeData";
import {useDrillholeDetails} from "../components/hooks/useDrillholeDetails";
import {useSelector, useDispatch} from 'react-redux';
import {RootState} from '../redux/store';
import * as THREE from 'three';
import ElementLegend from "../components/chart/ElementLegend";
import {Vector3} from "three";
import {getVectorOrder} from "../components/functions/Global";
import ControlBar from "./global/ControlBar";
import ViewAngleIndicator from "../components/chart/ViewAngleIndicator";

import {useTemplates} from "../components/hooks/useTemplates";
import {setBoreholes3DDataSources, setBoreholes3DActiveOverrides, DataSource, setColorGradientXrf, setColorGradientMinerals, setColorByElementXrf, setColorByElementMinerals, GradientStop} from "../redux/slices/ChartStateSlice";
import {elementKey, unitSuffix} from "../functions/drillholeTransform";
import {Input} from "@dmt/global_npm";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {Button} from "primereact/button";
import {ProgressSpinner} from "primereact/progressspinner";
import "../style/chart/CanvasContent.scss";

const MIN_RADIUS = 0.8;
const MAX_RADIUS = 2.5;
const GRAY_RADIUS = 0.5;

// Wraps children in a group that yaws (around Y) so its local +Z always points
// to the camera. Used to keep a split borehole / ring oriented so the seam
// runs vertically along the camera-facing side.
function BillboardY({worldX, worldZ, children}: {worldX: number; worldZ: number; children: React.ReactNode}) {
    const {camera} = useThree();
    const ref = useRef<THREE.Group>(null);
    useFrame(() => {
        if (!ref.current) return;
        const dx = camera.position.x - worldX;
        const dz = camera.position.z - worldZ;
        ref.current.rotation.y = Math.atan2(dx, dz);
    });
    return <group ref={ref}>{children}</group>;
}

interface Element {
    id: number;
    color: string;
    name: string;
    unit?: string;
}

const offset = 150;
const height = 200;

// Helper function to convert lat/lon to 3D coordinates.
// X is negated so that, with the default camera (looking from +X/+Y/-Z toward
// origin), East appears to the right and North to the top — matching the map.
// This mirrors the convention used by Chart's getVectorOrder.
function latLonTo3D(lat: number, lon: number, centerLat: number, centerLon: number): [number, number] {
    const earthRadius = 6371000; // meters
    const latRad = (lat * Math.PI) / 180;
    const lonRad = (lon * Math.PI) / 180;
    const centerLatRad = (centerLat * Math.PI) / 180;
    const centerLonRad = (centerLon * Math.PI) / 180;

    const x = -earthRadius * (lonRad - centerLonRad) * Math.cos(centerLatRad);
    const z = earthRadius * (latRad - centerLatRad);

    return [x / 1000, z / 1000]; // Convert to kilometers for better scale
}

function SceneGrid({bounds}: {bounds: {minX: number, maxX: number, minZ: number, maxZ: number}}) {
    const padding = 20;
    const width = Math.max((bounds.maxX - bounds.minX) + padding * 2, 40);
    const depth = Math.max((bounds.maxZ - bounds.minZ) + padding * 2, 40);
    const size = Math.max(width, depth);
    const segments = Math.max(10, Math.min(100, Math.round(size / 5)));
    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerZ = (bounds.minZ + bounds.maxZ) / 2;
    return <gridHelper args={[size, segments, '#888888', '#cccccc']} position={[centerX, -0.05, centerZ]} />;
}

const INITIAL_CAMERA_POSITION: [number, number, number] = [200, 150, -200];
const INITIAL_CAMERA_TARGET: [number, number, number] = [0, 0, 0];

// Reports camera distance to parent via callback (throttled)
function CameraZoomTracker({onDistanceChange, onCameraChange, onAnglesChange, cameraState, resetView, fitView}: {
    onDistanceChange: (distance: number) => void,
    onCameraChange?: (camera: { position: [number, number, number], target: [number, number, number] }) => void,
    onAnglesChange?: (azimuth: number, polar: number) => void,
    cameraState?: { position: [number, number, number], target: [number, number, number] } | null,
    resetView?: boolean,
    fitView?: { position: [number, number, number], target: [number, number, number] } | null,
}) {
    const {camera, controls}: any = useThree();
    const lastRef = useRef(0);
    const resetFirstRunRef = useRef(true);
    const didInitialFitRef = useRef(false);
    // Keep latest fitView accessible without retriggering the reset effect.
    const fitViewRef = useRef(fitView);
    useEffect(() => { fitViewRef.current = fitView; }, [fitView]);

    // Restore camera state
    useEffect(() => {
        if (cameraState && controls) {
            camera.position.set(...cameraState.position);
            controls.target.set(...cameraState.target);
            camera.updateProjectionMatrix();
            controls.update();
            didInitialFitRef.current = true;
        }
    }, [cameraState, camera, controls]);

    // Auto-fit camera once when bounds-based fitView first becomes available.
    // Skipped if a template camera is being restored.
    useEffect(() => {
        if (didInitialFitRef.current) return;
        if (!controls || !fitView) return;
        if (cameraState) return;
        camera.position.set(...fitView.position);
        controls.target.set(...fitView.target);
        camera.updateProjectionMatrix();
        controls.update();
        didInitialFitRef.current = true;
    }, [fitView, controls, cameraState, camera]);

    // Reset to fit view on toggle of resetView (falls back to the static
    // initial position when no boreholes are loaded yet). Skip the first run
    // so the button's initial value doesn't clobber a restored template camera.
    useEffect(() => {
        if (resetFirstRunRef.current) {
            resetFirstRunRef.current = false;
            return;
        }
        if (!controls) return;
        const fv = fitViewRef.current;
        const pos = fv?.position ?? INITIAL_CAMERA_POSITION;
        const tgt = fv?.target ?? INITIAL_CAMERA_TARGET;
        camera.position.set(...pos);
        controls.target.set(...tgt);
        camera.updateProjectionMatrix();
        controls.update();
    }, [resetView, camera, controls]);

    // Track camera changes
    useEffect(() => {
        if (!controls) return;
        const handleChange = () => {
            onCameraChange?.({
                position: [camera.position.x, camera.position.y, camera.position.z],
                target: [controls.target.x, controls.target.y, controls.target.z]
            });
            if (controls.getAzimuthalAngle && controls.getPolarAngle) {
                onAnglesChange?.(controls.getAzimuthalAngle(), controls.getPolarAngle());
            }
        };
        controls.addEventListener('change', handleChange);
        // Emit once so widgets initialise with the current view angles.
        handleChange();
        return () => controls.removeEventListener('change', handleChange);
    }, [camera, controls, onCameraChange, onAnglesChange]);

    useFrame(() => {
        const d = camera.position.length();
        if (Math.abs(d - lastRef.current) > 0.5) {
            lastRef.current = d;
            onDistanceChange(d);
        }
    });
    return null;
}

const DONUT_MIN_TUBE = 0.15; // minimum donut thickness (width)
const DONUT_MAX_TUBE = 1.2;  // maximum donut thickness (width)
const DONUT_GAP = 0.05;      // gap between concentric donuts
const DONUT_HEIGHT = 0.6;    // used for close-button positioning

// Animates camera to look at a target position, syncs OrbitControls target
function CameraAnimator({target, controlsRef}: {
    target: THREE.Vector3 | null;
    controlsRef: React.RefObject<any>;
}) {
    const {camera} = useThree();
    const animating = useRef(false);
    const startTime = useRef(0);
    const startPos = useRef(new THREE.Vector3());
    const startLookAt = useRef(new THREE.Vector3());
    const endPos = useRef(new THREE.Vector3());
    const prevTarget = useRef<string>('');

    useEffect(() => {
        if (!target) return;
        const key = `${target.x},${target.y},${target.z}`;
        if (key === prevTarget.current) return;
        prevTarget.current = key;

        // Calculate camera destination: keep current viewing direction, zoom closer
        const dir = new THREE.Vector3().subVectors(camera.position, target).normalize();
        const distance = 40;
        endPos.current.copy(target).add(dir.multiplyScalar(distance));
        endPos.current.y = target.y + 20;

        startPos.current.copy(camera.position);
        if (controlsRef.current) {
            startLookAt.current.copy(controlsRef.current.target);
        }
        startTime.current = performance.now();
        animating.current = true;
    }, [target, camera, controlsRef]);

    useFrame(() => {
        if (!animating.current || !target) return;

        const elapsed = (performance.now() - startTime.current) / 1000;
        const duration = 0.8;
        const t = Math.min(elapsed / duration, 1);
        const ease = 1 - Math.pow(1 - t, 3);

        camera.position.lerpVectors(startPos.current, endPos.current, ease);

        // Animate OrbitControls target to the explosion center
        if (controlsRef.current) {
            controlsRef.current.target.lerpVectors(startLookAt.current, target, ease);
            controlsRef.current.update();
        }

        if (t >= 1) {
            animating.current = false;
        }
    });

    return null;
}

// Single donut ring element with tooltip at mouse position.
// When `half` is set, only the camera-facing left/right half of the torus is
// rendered (the parent must be billboarded so local +Z faces the camera).
function ExplosionDonut({item, onHover, half}: {
    item: any;
    onHover: (data: any | null) => void;
    half?: 'left' | 'right';
}) {
    const isFull = !half;
    // For the half rings, rotate the in-plane torus so its arc starts at the
    // camera-facing seam (local +Z): -π/2 around Y for the LEFT half (sweeps
    // through -X toward -Z) and +π/2 for the RIGHT half (sweeps through +X).
    const yaw = half === 'right' ? Math.PI / 2 : half === 'left' ? -Math.PI / 2 : 0;
    return (
        <mesh
            rotation={[Math.PI / 2, yaw, 0]}
            onPointerOver={(e: any) => { e.stopPropagation(); onHover(item); }}
            onPointerOut={() => onHover(null)}
        >
            <torusGeometry args={[item.ringRadius, item.tubeRadius, 4, 48, isFull ? Math.PI * 2 : Math.PI]} />
            <meshStandardMaterial
                color={item.element?.color || '#999'}
                transparent
                opacity={0.8}
                side={2}
            />
        </mesh>
    );
}

// Explosion view: concentric donut rings emanating from the borehole column.
// `speSperesBySource` carries the click target's measurement data per source.
// When both sources are present the rings render as half-tori so each source
// sits on its own camera-facing half (left vs. right by `dataSources` order).
function ExplosionView({position, speSperesBySource, elements, dataSources, onClose, onHoverElement, rotation}: {
    position: THREE.Vector3;
    speSperesBySource: Partial<Record<DataSource, any[]>>;
    elements: Element[];
    dataSources: DataSource[];
    onClose: () => void;
    onHoverElement: (data: any | null) => void;
    rotation: {azimuthRad: number; tiltRad: number} | null;
}) {
    const buildVisible = (sperseList: any[] | undefined, source: DataSource) => {
        if (!sperseList || sperseList.length === 0) return [] as any[];
        const sourceElements = elements.filter((e: any) => ((e as any).source ?? 'xrf') === source);
        const activeElementIds = new Set(sourceElements.filter((e: any) => e.active).map((e: any) => e.id));
        const items = sperseList
            .filter((s: any) => s.value > 0 && activeElementIds.has(s.element_id))
            .sort((a: any, b: any) => b.value - a.value);
        const maxValue = items.length > 0 ? items[0].value : 1;
        let currentOffset = GRAY_RADIUS + DONUT_GAP;
        return items.map((s: any) => {
            const element = sourceElements.find(e => e.id === s.element_id);
            const ratio = s.value / maxValue;
            const tubeRadius = DONUT_MIN_TUBE + ratio * (DONUT_MAX_TUBE - DONUT_MIN_TUBE);
            const ringRadius = currentOffset + tubeRadius;
            currentOffset = ringRadius + tubeRadius + DONUT_GAP;
            return {...s, element, ratio, tubeRadius, ringRadius};
        });
    };

    const visibleXrf = useMemo(() => buildVisible(speSperesBySource.xrf, 'xrf'), [speSperesBySource.xrf, elements]);
    const visibleMinerals = useMemo(() => buildVisible(speSperesBySource.minerals, 'minerals'), [speSperesBySource.minerals, elements]);

    const bothPresent = visibleXrf.length > 0 && visibleMinerals.length > 0;
    if (!bothPresent && visibleXrf.length === 0 && visibleMinerals.length === 0) return null;

    // When only one source has data we keep the existing full-ring layout.
    const singleVisible = !bothPresent
        ? (visibleXrf.length > 0 ? visibleXrf : visibleMinerals)
        : null;

    const xrfHalf: 'left' | 'right' = dataSources.indexOf('xrf') === 0 ? 'left' : 'right';
    const mineralsHalf: 'left' | 'right' = xrfHalf === 'left' ? 'right' : 'left';

    const azimuthRad = rotation?.azimuthRad ?? 0;
    const tiltRad = rotation?.tiltRad ?? 0;

    return (
        <group position={[position.x, position.y, position.z]}>
        <group rotation={[0, azimuthRad, 0]}>
        <group rotation={[tiltRad, 0, 0]}>
            {/* Close button */}
            <Html center position={[0, DONUT_HEIGHT + 2, 0]}>
                <button
                    onClick={(e) => { e.stopPropagation(); onClose(); }}
                    style={{
                        background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none',
                        borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer',
                        fontSize: '12px', lineHeight: '20px', padding: 0,
                    }}
                >×</button>
            </Html>

            {singleVisible
                ? singleVisible.map((item: any, index: number) => (
                    <ExplosionDonut key={`single-${index}`} item={item} onHover={onHoverElement}/>
                ))
                : (
                    <BillboardY worldX={position.x} worldZ={position.z}>
                        {visibleXrf.map((item: any, index: number) => (
                            <ExplosionDonut key={`xrf-${index}`} item={item} onHover={onHoverElement} half={xrfHalf}/>
                        ))}
                        {visibleMinerals.map((item: any, index: number) => (
                            <ExplosionDonut key={`min-${index}`} item={item} onHover={onHoverElement} half={mineralsHalf}/>
                        ))}
                    </BillboardY>
                )}
        </group>
        </group>
        </group>
    );
}

function TickLabel({tick, cameraDistance}: { tick: number; cameraDistance: number }) {
    return (
        <Html
            position={[-0.7, 0, 0]}
            center
            style={{
                pointerEvents: 'none',
                userSelect: 'none',
                color: '#333',
                fontSize: cameraDistance < 100 ? '14px' : '12px',
                fontFamily: 'monospace',
                whiteSpace: 'nowrap',
                background: 'rgba(255,255,255,0.7)',
                padding: '1px 3px',
                borderRadius: '2px',
            }}
        >
            {tick?.toFixed(tick % 1 === 0 ? 0 : 1) ?? '0'}m
        </Html>
    );
}

function DepthAxis({depth, cameraDistance}: { depth: number; cameraDistance: number }) {
    const { camera } = useThree();
    const groupRef = useRef<THREE.Group>(null);

    const ticks = useMemo(() => {
        if (depth <= 0) return [];

        // Logic for tick intervals based on camera distance
        let interval = 10;
        if (cameraDistance < 20) interval = 0.5;
        else if (cameraDistance < 50) interval = 1;
        else if (cameraDistance < 100) interval = 2;
        else if (cameraDistance < 250) interval = 5;
        else if (cameraDistance < 500) interval = 10;
        else if (cameraDistance < 1000) interval = 25;
        else interval = 50;

        const result = [];
        for (let i = 0; i <= depth; i += interval) {
            result.push(i);
        }

        return result;
    }, [depth, cameraDistance]);

    useFrame(() => {
        if (!groupRef.current) return;

        // Vector from borehole to camera in world coordinates
        const worldPos = new THREE.Vector3();
        groupRef.current.parent?.getWorldPosition(worldPos);

        const boreholeToCamera = new THREE.Vector3().subVectors(camera.position, worldPos);
        boreholeToCamera.y = 0; // Keep only the horizontal direction
        boreholeToCamera.normalize();

        // The "left" vector is perpendicular to the direction from borehole to camera (Up x Direction)
        // In Three.js (right-handed system): Up (0,1,0) x Direction (Z-ish) gives Right.
        // To get Left, we use Direction x Up.
        const leftVector = new THREE.Vector3().crossVectors(boreholeToCamera, new THREE.Vector3(0, 1, 0)).normalize();

        // Scale distance: we want a constant visual offset.
        const baseOffset = 5.0;
        const dynamicOffset = baseOffset * (cameraDistance / 100); // 100 as base
        const finalOffset = Math.max(3.0, dynamicOffset);

        groupRef.current.position.set(
            leftVector.x * finalOffset,
            0,
            leftVector.z * finalOffset
        );

        // Optional: always orient the axis toward the camera (billboard effect for the whole group)
        // Here, positioning to the left of the borehole is sufficient.
    });

    return (
        <group ref={groupRef}>
            {/* Vertikale Achse */}
            <mesh position={[0, -depth / 2, 0]}>
                <boxGeometry args={[0.1, depth, 0.1]}/>
                <meshStandardMaterial color="#333333"/>
            </mesh>

            {/* Ticks und Labels */}
            {ticks.map((tick) => (
                <group key={tick} position={[0, -tick, 0]}>
                    <mesh position={[-0.25, 0, 0]}>
                        <boxGeometry args={[0.5, 0.08, 0.08]}/>
                        <meshStandardMaterial color="#333333"/>
                    </mesh>
                    <TickLabel tick={tick} cameraDistance={cameraDistance} />
                </group>
            ))}
        </group>
    );
}

// Component for rendering a single borehole column
const BoreholeColumn = memo(({drillhole, elements, activeElement, position, cameraDistance, onSectionClick, cameraMovedRef, showDepthAxis, dataSources, useAngle, boreholeCount}: {
    drillhole: any;
    elements: Element[];
    activeElement: any;
    position: [number, number, number];
    cameraDistance: number;
    onSectionClick: (item: any, worldPosition: THREE.Vector3, depthFrom: number, depthTo: number, index: number, sections: any[], basePosition: [number, number, number], rotation: {azimuthRad: number; tiltRad: number} | null) => void;
    cameraMovedRef: React.MutableRefObject<boolean>;
    showDepthAxis: boolean;
    dataSources: DataSource[];
    useAngle: boolean;
    boreholeCount: number;
}) => {
    const [hoverData, setHoverData] = useState<{ depthFrom: number; depthTo: number; topElement: any } | null>(null);

    // Calculate quality (segments) based on distance and number of boreholes
    const radialSegments = useMemo(() => {
        if (boreholeCount <= 3) return 24; // High quality for few boreholes
        if (cameraDistance > 200) return 6; // Low quality when far away
        if (cameraDistance > 100) return 12; // Medium quality
        return 24; // High quality when close
    }, [cameraDistance, boreholeCount]);
    const sections = useMemo(() => {
        if (!drillhole.dri_mea || drillhole.dri_mea.length === 0) {
            return [];
        }

        // Precompute per-source filter state so we can use it both for the
        // max-value sweep and the final section build.
        const sourceMeta = new Map<string, { activeElementIds: Set<number>; sourceElements: Element[]; activeForSource: any }>();
        const getMeta = (src: string) => {
            let m = sourceMeta.get(src);
            if (!m) {
                const sourceElements = elements.filter((e: any) => (e.source ?? 'xrf') === src);
                const activeElementIds = new Set(sourceElements.filter((e: any) => e.active).map((e: any) => e.id));
                const activeForSource = activeElement && (activeElement.source ?? 'xrf') === src ? activeElement : null;
                m = {sourceElements, activeElementIds, activeForSource};
                sourceMeta.set(src, m);
            }
            return m;
        };

        // Compute maxAmount from the values that will actually be rendered as
        // colored sections. Using drillhole.greatest_amount_value would include
        // hidden sources/elements (e.g. XRF max when all XRF switches are off),
        // which collapses the remaining sections toward MIN_RADIUS.
        let maxAmount = 0;
        for (const mea of drillhole.dri_mea) {
            if (!mea.mea_xrfres) continue;
            const {activeElementIds, activeForSource} = getMeta(mea.__source ?? 'xrf');
            for (const meaXrfres of mea.mea_xrfres) {
                for (const xrfresSpe of meaXrfres.xrfres_spe) {
                    if (activeForSource) {
                        const a = xrfresSpe.spe_speres?.find((x: any) => x.element_id === activeForSource.id);
                        if (a && a.value > maxAmount) maxAmount = a.value;
                    } else if (!activeElement) {
                        for (const speres of (xrfresSpe.spe_speres || [])) {
                            if (!activeElementIds.has(speres.element_id)) continue;
                            if (speres.value > maxAmount) maxAmount = speres.value;
                        }
                    }
                }
            }
        }
        if (maxAmount <= 0) maxAmount = 1;

        const result: any[] = [];

        for (const mea of drillhole.dri_mea) {
            if (!mea.mea_xrfres) continue;
            const meaSource = mea.__source ?? 'xrf';
            const {sourceElements, activeElementIds, activeForSource} = getMeta(meaSource);

            for (const meaXrfres of mea.mea_xrfres) {
                for (const xrfresSpe of meaXrfres.xrfres_spe) {

                    let amount: any = null;
                    let color = '#666666';

                    if (activeForSource) {
                        amount = xrfresSpe.spe_speres?.find((x: any) => x.element_id === activeForSource.id);
                        if (amount && amount.value > 0) {
                            color = activeForSource.color;
                        } else {
                            amount = null;
                        }
                    } else if (!activeElement) {
                        // Find dominant element among active elements of this source
                        let best: any = null;
                        let bestElement: any = null;
                        for (const speres of (xrfresSpe.spe_speres || [])) {
                            if (!activeElementIds.has(speres.element_id)) continue;
                            if (speres.value > 0 && (!best || speres.value > best.value)) {
                                best = speres;
                                bestElement = sourceElements.find((e: any) => e.id === speres.element_id);
                            }
                        }
                        color = bestElement ? bestElement.color : '#666666';
                        amount = best;
                    }

                    const height = Math.abs(meaXrfres.stop_position - meaXrfres.start_position) || 0.1;
                    const yPosition = -(meaXrfres.start_position + meaXrfres.stop_position) / 2;

                    const amountValue = amount ? amount.value : 0;
                    const ratio = amountValue > 0 ? Math.min(amountValue / maxAmount, 1) : 0;
                    const radius = MIN_RADIUS + ratio * (MAX_RADIUS - MIN_RADIUS);

                    result.push({
                        height,
                        yPosition,
                        color,
                        radius,
                        hasAmount: amount != null && amountValue > 0,
                        startPos: meaXrfres.start_position,
                        stopPos: meaXrfres.stop_position,
                        item: meaXrfres.xrfres_spe,
                        source: meaSource,
                    });
                }
            }
        }

        return result;
    }, [drillhole, elements, activeElement]);

    // When both XRF and Minerals have at least one active element, the borehole
    // sections are rendered as half cylinders so each source occupies its own
    // camera-facing side.
    const bothSourcesActive = useMemo(() => {
        const set = new Set<string>();
        for (const e of elements) {
            if ((e as any).active) set.add(((e as any).source ?? 'xrf') as string);
        }
        return set.has('xrf') && set.has('minerals');
    }, [elements]);

    const depth = useMemo(() => {
        let result = 0;
        if (drillhole && drillhole.dri_dripos && drillhole.dri_dripos.length > 0) {

            const highestDepth = drillhole.dri_dripos.reduce((acc: any, dripos: any) => {
                return dripos.depth > acc.depth ? dripos : acc;
            });

            if (highestDepth) {
                result = highestDepth.depth;
            }
        }

        return result;
    }, [drillhole])

    // Borehole angle: rotate the column to match the drillhole's azimuth and dip
    // when useAngle is on. World axes here: +Z = North, -X = East (see latLonTo3D).
    // Azimuth 0° points North (+Z); a Y-rotation by -azimuth sends local -Z (i.e.
    // after the dip-tilt that puts the cylinder's local -Y into the +Z hemisphere)
    // to the correct compass bearing. A negative dip (e.g. -90 = vertical) becomes
    // a tilt of (|dip|−90)° around X: -90 → 0 (straight down), -45 → -45° tilt.
    const {azimuthRad, tiltRad} = useMemo(() => {
        if (!useAngle) return {azimuthRad: 0, tiltRad: 0};
        const first = drillhole?.dri_dripos?.[0];
        if (!first) return {azimuthRad: 0, tiltRad: 0};
        const azimuthDeg = first.azimuth ?? 0;
        const dipDeg = first.deviation ?? -90;
        return {
            azimuthRad: -azimuthDeg * Math.PI / 180,
            tiltRad: (Math.abs(dipDeg) - 90) * Math.PI / 180,
        };
    }, [useAngle, drillhole]);

    const localToWorld = (yLocal: number): THREE.Vector3 => {
        const v = new THREE.Vector3(0, yLocal, 0);
        if (useAngle) {
            v.applyAxisAngle(new THREE.Vector3(1, 0, 0), tiltRad);
            v.applyAxisAngle(new THREE.Vector3(0, 1, 0), azimuthRad);
        }
        return new THREE.Vector3(position[0] + v.x, position[1] + v.y, position[2] + v.z);
    };

    return (
        <group position={position}>
        <group rotation={[0, azimuthRad, 0]}>
        <group rotation={[tiltRad, 0, 0]}>
            <Html center className={'label unselectable'}
                  position={[0, 10, 0]}>
                <label>{drillhole.id}</label>
            </Html>
            {showDepthAxis && depth && depth > 0 && (
                <DepthAxis depth={depth} cameraDistance={cameraDistance} />
            )}
            {depth &&
                <Cylinder
                    key={'depth_cylinder'}
                    args={[GRAY_RADIUS, GRAY_RADIUS, depth, radialSegments]}
                    position={[0, -depth / 2, 0]}
                >
                    <meshStandardMaterial color="#aaaaaa" side={2} depthTest={true} depthWrite={true}/>
                </Cylinder>}

            {(bothSourcesActive ? (
                <>
                    {sections.map((section: any, index: number) => {
                        const sourceIdx = dataSources.indexOf(section.source);
                        // CylinderGeometry: theta=0..π is the +Z→+X→-Z half (local "front"),
                        // theta=π..2π is the -Z→-X→+Z half (local "back"). Split is fixed in
                        // the borehole's local frame — first DataSource on the back half,
                        // second on the front half.
                        const thetaStart = sourceIdx === 0 ? Math.PI : 0;
                        const handlePointerOver = (event: any) => {
                            event.stopPropagation();
                            let topElement = null;
                            if (section.item && section.item.length > 0) {
                                const source = section.source ?? 'xrf';
                                const sourceElements = elements.filter((e: any) => ((e as any).source ?? 'xrf') === source);
                                const activeElementIds = new Set(sourceElements.filter((e: any) => e.active).map((e: any) => e.id));
                                let bestItem: any = null;
                                for (const spe of section.item) {
                                    if (!spe.spe_speres) continue;
                                    for (const speres of spe.spe_speres) {
                                        if (speres.value > 0 && activeElementIds.has(speres.element_id)) {
                                            if (!bestItem || speres.value > bestItem.value) bestItem = speres;
                                        }
                                    }
                                }
                                if (bestItem) {
                                    const element = sourceElements.find(e => e.id === bestItem.element_id);
                                    topElement = {
                                        name: element?.name || bestItem.element_id,
                                        value: bestItem.value,
                                        unit: element?.unit,
                                        color: element?.color || '#ffffff'
                                    };
                                }
                            }
                            setHoverData({depthFrom: section.startPos, depthTo: section.stopPos, topElement});
                        };
                        const handlePointerOut = () => setHoverData(null);
                        return (
                            <Cylinder
                                key={`split-${section.source}-${index}`}
                                args={[section.radius, section.radius, section.height, radialSegments, 1, false, thetaStart, Math.PI]}
                                position={[0, section.yPosition, 0]}
                                onPointerOver={handlePointerOver}
                                onPointerOut={handlePointerOut}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    if (cameraMovedRef.current) return;
                                    const worldPosition = localToWorld(section.yPosition);
                                    onSectionClick(section.item, worldPosition, section.startPos, section.stopPos, index, sections, [position[0], position[1], position[2]], useAngle ? {azimuthRad, tiltRad} : null);
                                }}
                            >
                                <meshStandardMaterial
                                    color={section.hasAmount ? section.color : '#cccccc'}
                                    side={2}
                                    depthTest={true}
                                    depthWrite={true}
                                />
                            </Cylinder>
                        );
                    })}
                </>
            ) : sections.map((section: any, index) => {
                const handlePointerOver = (event: any) => {
                    event.stopPropagation();

                    // Find the most frequent element (top element)
                    let topElement = null;
                    if (section.item && section.item.length > 0) {
                        const source = section.source ?? 'xrf';
                        const sourceElements = elements.filter((e: any) => ((e as any).source ?? 'xrf') === source);
                        const activeElementIds = new Set(sourceElements.filter((e: any) => e.active).map((e: any) => e.id));

                        let bestItem: any = null;

                        // Iterate over all spectra in the section (usually just one)
                        for (const spe of section.item) {
                            if (!spe.spe_speres) continue;
                            for (const speres of spe.spe_speres) {
                                if (speres.value > 0 && activeElementIds.has(speres.element_id)) {
                                    if (!bestItem || speres.value > bestItem.value) {
                                        bestItem = speres;
                                    }
                                }
                            }
                        }

                        if (bestItem) {
                            const element = sourceElements.find(e => e.id === bestItem.element_id);
                            topElement = {
                                name: element?.name || bestItem.element_id,
                                value: bestItem.value,
                                unit: element?.unit,
                                color: element?.color || '#ffffff'
                            };
                        }
                    }

                    setHoverData({
                        depthFrom: section.startPos,
                        depthTo: section.stopPos,
                        topElement
                    });
                };

                const handlePointerOut = () => {
                    setHoverData(null);
                };

                return (
                    <Cylinder
                        key={index}
                        args={[section.radius, section.radius, section.height, radialSegments]}
                        position={[0, section.yPosition, 0]}
                        onPointerOver={handlePointerOver}
                        onPointerOut={handlePointerOut}
                        onClick={(event) => {
                            event.stopPropagation();
                            if (cameraMovedRef.current) return;
                            const worldPosition = localToWorld(section.yPosition);
                            onSectionClick(section.item, worldPosition, section.startPos, section.stopPos, index, sections, [position[0], position[1], position[2]], useAngle ? {azimuthRad, tiltRad} : null);
                        }}
                    >
                        <meshStandardMaterial
                            color={section.hasAmount ? section.color : '#cccccc'}
                            depthTest={true}
                            depthWrite={true}
                        />
                    </Cylinder>
                );
            }))}

            {hoverData && (
                <Html
                    position={[0, -(hoverData.depthFrom + hoverData.depthTo) / 2, 0]}
                    className="amount"
                    style={{ pointerEvents: 'none' }}
                >
                    <div className="amount-tooltip" style={{
                        position: 'relative',
                        left: '20px',
                        top: '0',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none'
                    }}>
                        <div className="depth-info">
                            Depth: {hoverData.depthFrom?.toFixed(2).replace('.', ',') ?? '0,00'} – {hoverData.depthTo?.toFixed(2).replace('.', ',') ?? '0,00'} m
                        </div>
                        {hoverData.topElement ? (
                            <div className="element-info">
                                <div className="swatch" style={{backgroundColor: hoverData.topElement.color}} />
                                <span className="name">{hoverData.topElement.name}</span>
                                <span className="value">{(hoverData.topElement.value?.toFixed(2).replace('.', ',') ?? '0,00') + unitSuffix(hoverData.topElement.unit)}</span>
                            </div>
                        ) : (
                            <div style={{fontStyle: 'italic', opacity: 0.7}}>No data</div>
                        )}
                    </div>
                </Html>
            )}
        </group>
        </group>
        </group>
    )
});

// Scene background component
function SceneBackground() {
    return (
        <group>
            {/* Bottom plane */}
            {/*<mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -100, 0]}>*/}
            {/*    <planeGeometry args={[offset*2, offset*2]}/>*/}
            {/*    <meshStandardMaterial color="#d3d3d3"/>*/}
            {/*</mesh>*/}

            {/* Side walls */}
            <mesh position={[offset * -1, -height / 2, 0]} rotation={[0, Math.PI / 2, 0]}>
                <planeGeometry args={[offset * 2, height]}/>
                <meshStandardMaterial color="#d3d3d3"/>
            </mesh>

            <mesh position={[offset, -height / 2, 0]} rotation={[0, -Math.PI / 2, 0]}>
                <planeGeometry args={[offset * 2, height]}/>
                <meshStandardMaterial color="#d3d3d3"/>
            </mesh>

            <mesh position={[0, -height / 2, offset * -1]}>
                <planeGeometry args={[offset * 2, height]}/>
                <meshStandardMaterial color="#d3d3d3"/>
            </mesh>

            <mesh position={[0, -height / 2, offset]} rotation={[0, -Math.PI / 1, 0]}>
                <planeGeometry args={[offset * 2, height]}/>
                <meshStandardMaterial color="#d3d3d3"/>
            </mesh>
        </group>
    );
}

export default function Boreholes3D() {
    const {data, withCoords, isLoading: listLoading} = useDrillholeData();
    const {fetchDetails} = useDrillholeDetails();
    const selectedBoreholesRaw = useSelector((state: RootState) => state.oldDesignState.selectedBoreholes);
    const [selectedDrillholeIds, setSelectedDrillholeIds] = useState<string[]>(() => {
        try {
            const raw = localStorage.getItem('boreholes3DSelectedDrillholes');
            if (raw) {
                const arr = JSON.parse(raw);
                if (Array.isArray(arr)) return arr;
            }
        } catch (_) {}
        return [];
    });
    const isInitialMount = useRef(true);
    const xrfElements = useSelector((state: RootState) => state.oldDesignState.xrfElements);
    const mineralsElements = useSelector((state: RootState) => state.oldDesignState.mineralsElements);
    const dataSources = useSelector((state: RootState) => state.oldDesignState.boreholes3DDataSources) as DataSource[];
    const activeOverrides = useSelector((state: RootState) => state.oldDesignState.boreholes3DActiveOverrides) as Record<string, boolean>;
    const colorGradientXrf = useSelector((state: RootState) => state.oldDesignState.colorGradientXrf) as GradientStop[];
    const colorGradientMinerals = useSelector((state: RootState) => state.oldDesignState.colorGradientMinerals) as GradientStop[];
    const colorByElementXrf = useSelector((state: RootState) => state.oldDesignState.colorByElementXrf) as boolean;
    const colorByElementMinerals = useSelector((state: RootState) => state.oldDesignState.colorByElementMinerals) as boolean;
    const elements = useMemo(() => {
        const merge = (el: any) => {
            const key = elementKey(el);
            const overridden = Object.prototype.hasOwnProperty.call(activeOverrides, key);
            return overridden ? {...el, active: !!activeOverrides[key]} : el;
        };
        const out: any[] = [];
        if (dataSources.includes('xrf')) out.push(...xrfElements.map(merge));
        if (dataSources.includes('minerals')) out.push(...mineralsElements.map(merge));
        return out;
    }, [dataSources, xrfElements, mineralsElements, activeOverrides]);
    const activeElement = useSelector((state: RootState) => state.oldDesignState.activeElement);
    const isControlBarCollapsed = useSelector((state: RootState) => state.oldDesignState.isControlBarCollapsed);
    const selectLine = useSelector((state: any) => state.oldDesignState.selectLine);
    const mapBufferOffset = useSelector((state: any) => state.oldDesignState.offset);

    // Build a combined dri_mea array, each entry tagged with its __source so
    // downstream renderers can match measurement values against elements of the
    // same source (XRF element ids can collide with minerals element ids).
    const selectedBoreholes = useMemo(() => {
        if (!selectedBoreholesRaw) return [];
        const wantXrf = dataSources.includes('xrf');
        const wantMinerals = dataSources.includes('minerals');
        return selectedBoreholesRaw.map((bh: any) => {
            const xrfMea = wantXrf
                ? (bh.dri_mea ?? []).map((m: any) => ({...m, __source: 'xrf' as const}))
                : [];
            const minMea = wantMinerals
                ? (bh.dri_mea_minerals ?? []).map((m: any) => ({...m, __source: 'minerals' as const}))
                : [];
            return {
                ...bh,
                dri_mea: [...xrfMea, ...minMea],
                greatest_amount_value: Math.max(
                    wantXrf ? (bh.greatest_amount_value ?? 0) : 0,
                    wantMinerals ? (bh.greatest_amount_value_minerals ?? 0) : 0,
                ) || 1,
                greatest_amount_value_xrf: bh.greatest_amount_value ?? 0,
                greatest_amount_value_minerals: bh.greatest_amount_value_minerals ?? 0,
            };
        });
    }, [selectedBoreholesRaw, dataSources]);
    const controlsRef = useRef<any>(null);
    const [selectedSection, setSelectedSection] = useState<any>(null);
    const [selectedPosition, setSelectedPosition] = useState<THREE.Vector3 | null>(null);
    const [explosionData, setExplosionData] = useState<any[] | null>(null);
    const [explosionSource, setExplosionSource] = useState<DataSource>('xrf');
    // Sperse data for both sources at the clicked depth — feeds the split
    // ring view when XRF and Minerals are both active.
    const [explosionDataBySource, setExplosionDataBySource] = useState<Partial<Record<DataSource, any[]>> | null>(null);
    const [showPopup, setShowPopup] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [tooltip, setTooltip] = useState<any>(null);
    const [mousePos, setMousePos] = useState({x: 0, y: 0});
    const [sectionDepth, setSectionDepth] = useState<{from: number, to: number} | null>(null);
    const [windroseRotation, setWindroseRotation] = useState(0);
    const [polarAngle, setPolarAngle] = useState(Math.PI / 2);
    const [resetView, setResetView] = useState(false);
    const fetchedRef = useRef(false);
    const fetchInFlightRef = useRef<Set<string>>(new Set());
    const cameraMovedRef = useRef(false);
    const dispatch = useDispatch();

    const {templates, addTemplate, updateTemplate, deleteTemplate} = useTemplates<{
        selectedDrillholeIds: string[],
        dataSources: DataSource[],
        // Legacy: templates saved before multi-source
        dataSource?: DataSource,
        camera: { position: [number, number, number], target: [number, number, number] } | null,
        hideInactive?: boolean,
        showDepthAxis?: boolean,
        showGrid?: boolean,
        useBoreholeAngle?: boolean,
        nnOffset?: number,
        activeOverrides?: Record<string, boolean>,
        colorGradientXrf?: GradientStop[],
        colorGradientMinerals?: GradientStop[],
        colorByElementXrf?: boolean,
        colorByElementMinerals?: boolean,
    }>('boreholes3d_templates');

    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(() => {
        return localStorage.getItem('boreholes3DSelectedTemplateId');
    });
    const [isAddTemplateDialogVisible, setIsAddTemplateDialogVisible] = useState(false);
    const [newTemplateName, setNewTemplateName] = useState('');
    const [currentCamera, setCurrentCamera] = useState<{ position: [number, number, number], target: [number, number, number] } | null>(null);
    const [legendHideInactive, setLegendHideInactive] = useState<boolean>(() => {
        return localStorage.getItem('boreholes3DLegendHideInactive') === 'true';
    });
    const [showDepthAxis, setShowDepthAxis] = useState<boolean>(() => {
        const val = localStorage.getItem('boreholes3DShowDepthAxis');
        return val === null ? true : val === 'true';
    });
    const [showGrid, setShowGrid] = useState<boolean>(() => {
        const val = localStorage.getItem('boreholes3DShowGrid');
        return val === null ? true : val === 'true';
    });
    const [useBoreholeAngle, setUseBoreholeAngle] = useState<boolean>(() => {
        return localStorage.getItem('boreholes3DUseBoreholeAngle') === 'true';
    });
    const [nnOffset, setNnOffset] = useState<number>(() => {
        const raw = localStorage.getItem('boreholes3DNNOffset');
        if (raw == null) return 0;
        const parsed = Number(raw);
        return isFinite(parsed) ? parsed : 0;
    });
    const [restoringTemplateCamera, setRestoringTemplateCamera] = useState<{ position: [number, number, number], target: [number, number, number] } | null>(null);

    const handleApplyTemplate = (id: string) => {
        const template = templates.find(t => t.id === id);
        if (template) {
            setSelectedDrillholeIds(template.data.selectedDrillholeIds);
            const sources = template.data.dataSources
                ?? (template.data.dataSource ? [template.data.dataSource] : null);
            if (sources && sources.length > 0) dispatch(setBoreholes3DDataSources(sources));
            if (template.data.camera) {
                setRestoringTemplateCamera(template.data.camera);
            }
            setLegendHideInactive(!!template.data.hideInactive);
            setShowDepthAxis(template.data.showDepthAxis !== false);
            setShowGrid(template.data.showGrid !== false);
            setUseBoreholeAngle(!!template.data.useBoreholeAngle);
            setNnOffset(template.data.nnOffset ?? 0);
            dispatch(setBoreholes3DActiveOverrides(template.data.activeOverrides ?? {}));
            if (template.data.colorGradientXrf) dispatch(setColorGradientXrf(template.data.colorGradientXrf));
            if (template.data.colorGradientMinerals) dispatch(setColorGradientMinerals(template.data.colorGradientMinerals));
            if (typeof template.data.colorByElementXrf === 'boolean') dispatch(setColorByElementXrf(template.data.colorByElementXrf));
            if (typeof template.data.colorByElementMinerals === 'boolean') dispatch(setColorByElementMinerals(template.data.colorByElementMinerals));
            setSelectedTemplateId(id);
        }
    };

    const handleSaveNewTemplate = async () => {
        if (newTemplateName.trim()) {
            const existing = templates.find(t => t.name.toLowerCase() === newTemplateName.trim().toLowerCase());
            const data = {
                selectedDrillholeIds,
                dataSources,
                camera: currentCamera,
                hideInactive: legendHideInactive,
                showDepthAxis: showDepthAxis,
                showGrid: showGrid,
                useBoreholeAngle,
                nnOffset,
                activeOverrides,
                colorGradientXrf,
                colorGradientMinerals,
                colorByElementXrf,
                colorByElementMinerals,
            };
            if (existing) {
                await updateTemplate(existing.id, data);
                setSelectedTemplateId(existing.id);
            } else {
                const newTpl = await addTemplate(newTemplateName, data);
                if (newTpl) setSelectedTemplateId(newTpl.id);
            }
            setNewTemplateName('');
            setIsAddTemplateDialogVisible(false);
        }
    };

    const handleDeleteTemplate = () => {
        if (selectedTemplateId) {
            deleteTemplate(selectedTemplateId);
            setSelectedTemplateId(null);
        }
    };

    // Deselect if something changes
    useEffect(() => {
        if (selectedTemplateId) {
            const template = templates.find(t => t.id === selectedTemplateId);
            if (template) {
                const templateSources = template.data.dataSources
                    ?? (template.data.dataSource ? [template.data.dataSource] : []);
                const isSameDrillholes = JSON.stringify(template.data.selectedDrillholeIds) === JSON.stringify(selectedDrillholeIds);
                const isSameDataSources = JSON.stringify(templateSources) === JSON.stringify(dataSources);
                const isSameHideInactive = (template.data.hideInactive ?? false) === legendHideInactive;
                const isSameShowDepthAxis = (template.data.showDepthAxis !== false) === showDepthAxis;
                const isSameShowGrid = (template.data.showGrid !== false) === showGrid;
                const isSameUseBoreholeAngle = (!!template.data.useBoreholeAngle) === useBoreholeAngle;
                const isSameNnOffset = (template.data.nnOffset ?? 0) === nnOffset;
                const isSameActiveOverrides = JSON.stringify(template.data.activeOverrides ?? {}) === JSON.stringify(activeOverrides);
                const isSameGradientXrf = !template.data.colorGradientXrf
                    || JSON.stringify(template.data.colorGradientXrf) === JSON.stringify(colorGradientXrf);
                const isSameGradientMinerals = !template.data.colorGradientMinerals
                    || JSON.stringify(template.data.colorGradientMinerals) === JSON.stringify(colorGradientMinerals);
                const isSameColorByElementXrf = template.data.colorByElementXrf == null
                    || template.data.colorByElementXrf === colorByElementXrf;
                const isSameColorByElementMinerals = template.data.colorByElementMinerals == null
                    || template.data.colorByElementMinerals === colorByElementMinerals;
                if (!isSameDrillholes || !isSameDataSources || !isSameHideInactive || !isSameShowDepthAxis || !isSameShowGrid || !isSameUseBoreholeAngle || !isSameNnOffset || !isSameActiveOverrides || !isSameGradientXrf || !isSameGradientMinerals || !isSameColorByElementXrf || !isSameColorByElementMinerals) {
                    setSelectedTemplateId(null);
                }
            }
        }
    }, [selectedDrillholeIds, dataSources, legendHideInactive, showDepthAxis, showGrid, useBoreholeAngle, nnOffset, activeOverrides, colorGradientXrf, colorGradientMinerals, colorByElementXrf, colorByElementMinerals, templates, selectedTemplateId]);

    const containerRef = useRef<HTMLDivElement>(null);
    const [cameraDistance, setCameraDistance] = useState(0);
    const throttledCameraDistance = useMemo(() => {
        return Math.round(cameraDistance / 10) * 10;
    }, [cameraDistance]);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [currentSections, setCurrentSections] = useState<any[] | null>(null);
    const [basePosition, setBasePosition] = useState<[number, number, number] | null>(null);
    const [sectionRotation, setSectionRotation] = useState<{azimuthRad: number; tiltRad: number} | null>(null);

    // Initial bulk-fetch for ALL withCoords only when truly empty (no
    // localStorage selection and no Redux selection yet). Otherwise the
    // selection-driven effect below covers what's needed and we avoid
    // refetching the same holes twice.
    useEffect(() => {
        if (fetchedRef.current || !withCoords || withCoords.length === 0) return;
        if (selectedDrillholeIds.length > 0) return;
        if (selectedBoreholesRaw && selectedBoreholesRaw.length > 0) return;
        const idsToLoad = withCoords
            .map((d: any) => d.hole_id)
            .filter((id: string) => id && !fetchInFlightRef.current.has(id));
        if (idsToLoad.length === 0) return;
        const toLoad = withCoords.filter((d: any) => idsToLoad.includes(d.hole_id));
        fetchedRef.current = true;
        idsToLoad.forEach((id: string) => fetchInFlightRef.current.add(id));
        setDetailsLoading(true);
        fetchDetails(toLoad).finally(() => {
            idsToLoad.forEach((id: string) => fetchInFlightRef.current.delete(id));
            setDetailsLoading(false);
        });
    }, [withCoords, selectedDrillholeIds, selectedBoreholesRaw, fetchDetails]);

    // Sync Redux -> local IDs (nur beim ersten Laden, falls lokal nichts gespeichert ist)
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            if (selectedDrillholeIds.length === 0 && selectedBoreholesRaw && selectedBoreholesRaw.length > 0) {
                const ids = selectedBoreholesRaw.map((b: any) => b.hole_id).filter(Boolean);
                setSelectedDrillholeIds(ids);
            }
        }
    }, [selectedBoreholesRaw]);

    // Sync selectedDrillholeIds with the GeoMap line whenever the line changes.
    // The localStorage tracker ensures manual dropdown changes are preserved until
    // the line itself is changed on the map.
    useEffect(() => {
        if (!selectLine || !withCoords || withCoords.length === 0 || mapBufferOffset <= 0) {
            if (!selectLine) localStorage.removeItem('boreholes3DLastSyncedSelectLine');
            return;
        }
        const currentLineKey = JSON.stringify(selectLine);
        const lastSyncedKey = localStorage.getItem('boreholes3DLastSyncedSelectLine');
        if (currentLineKey === lastSyncedKey) return;

        const c1 = [selectLine.x1, selectLine.y1];
        const c2 = [selectLine.x2, selectLine.y2];
        const ls = turf.lineString([c1, c2]);
        const buff = turf.buffer(ls, mapBufferOffset / 1.6, {units: 'meters'});
        const ids: string[] = [];
        const capturedHoles: any[] = [];
        for (const item of withCoords) {
            if (!item.collar || item.collar.LONGITUDE == null || item.collar.LATITUDE == null) continue;
            const pt = turf.point([item.collar.LONGITUDE, item.collar.LATITUDE]);
            if (turf.booleanPointInPolygon(pt, buff)) {
                ids.push(item.hole_id);
                capturedHoles.push(item);
            }
        }
        setSelectedDrillholeIds(ids);
        if (capturedHoles.length > 0) {
            const inFlight = capturedHoles
                .map((h: any) => h.hole_id)
                .filter((id: string) => id && !fetchInFlightRef.current.has(id));
            inFlight.forEach((id: string) => fetchInFlightRef.current.add(id));
            setDetailsLoading(true);
            fetchDetails(capturedHoles, [selectLine.x1, selectLine.y1]).finally(() => {
                inFlight.forEach((id: string) => fetchInFlightRef.current.delete(id));
                setDetailsLoading(false);
            });
        }
        localStorage.setItem('boreholes3DLastSyncedSelectLine', currentLineKey);
    }, [selectLine, withCoords, mapBufferOffset, fetchDetails]);

    // Persistieren der Einstellungen
    useEffect(() => {
        localStorage.setItem('boreholes3DSelectedDrillholes', JSON.stringify(selectedDrillholeIds));
    }, [selectedDrillholeIds]);

    useEffect(() => {
        localStorage.setItem('boreholes3DLegendHideInactive', String(legendHideInactive));
    }, [legendHideInactive]);

    useEffect(() => {
        localStorage.setItem('boreholes3DShowDepthAxis', String(showDepthAxis));
    }, [showDepthAxis]);

    useEffect(() => {
        localStorage.setItem('boreholes3DShowGrid', String(showGrid));
    }, [showGrid]);

    useEffect(() => {
        localStorage.setItem('boreholes3DUseBoreholeAngle', String(useBoreholeAngle));
    }, [useBoreholeAngle]);

    useEffect(() => {
        localStorage.setItem('boreholes3DNNOffset', String(nnOffset));
    }, [nnOffset]);

    // When a NEW line is drawn on the map, seed nnOffset with the first
    // borehole's RELATIVE LEVEL. We persist the last-seen line so re-mounting
    // with the same line preserves the user's manual nnOffset.
    useEffect(() => {
        if (!selectLine || !withCoords || withCoords.length === 0 || mapBufferOffset <= 0) return;
        const currentLineKey = JSON.stringify(selectLine);
        const lastSeenKey = localStorage.getItem('boreholes3DLastSeenSelectLine');
        if (currentLineKey === lastSeenKey) return;

        const c1 = [selectLine.x1, selectLine.y1];
        const c2 = [selectLine.x2, selectLine.y2];
        const ls = turf.lineString([c1, c2]);
        const buff = turf.buffer(ls, mapBufferOffset / 1.6, {units: 'meters'});

        let firstBorehole: any = null;
        for (const item of withCoords) {
            if (!item.collar || item.collar.LONGITUDE == null || item.collar.LATITUDE == null) continue;
            const pt = turf.point([item.collar.LONGITUDE, item.collar.LATITUDE]);
            if (turf.booleanPointInPolygon(pt, buff)) {
                firstBorehole = item;
                break;
            }
        }

        const level = firstBorehole?.collar?.['RELATIVE LEVEL'];
        if (level != null && isFinite(level)) {
            setNnOffset(level);
        }
        localStorage.setItem('boreholes3DLastSeenSelectLine', currentLineKey);
    }, [selectLine, withCoords, mapBufferOffset]);

    useEffect(() => {
        if (selectedTemplateId) {
            localStorage.setItem('boreholes3DSelectedTemplateId', selectedTemplateId);
        } else {
            localStorage.removeItem('boreholes3DSelectedTemplateId');
        }
    }, [selectedTemplateId]);

    // Fetch details only for the missing selected drillholes. Skips IDs that
    // are already enriched in Redux or currently in flight (StrictMode-safe).
    useEffect(() => {
        if (selectedDrillholeIds.length === 0 || data.length === 0) return;
        const enrichedIds = new Set((selectedBoreholesRaw || []).map((b: any) => b.hole_id));
        const missing = selectedDrillholeIds.filter(id =>
            !enrichedIds.has(id) && !fetchInFlightRef.current.has(id)
        );
        if (missing.length === 0) return;
        const toLoad = missing
            .map(id => data.find((d: any) => d.hole_id === id))
            .filter(Boolean);
        if (toLoad.length === 0) return;
        missing.forEach(id => fetchInFlightRef.current.add(id));
        setDetailsLoading(true);
        fetchDetails(toLoad).finally(() => {
            missing.forEach(id => fetchInFlightRef.current.delete(id));
            setDetailsLoading(false);
        });
    }, [selectedDrillholeIds, data, selectedBoreholesRaw, fetchDetails]);

    const collectSperes = (item: any): any[] => {
        const out: any[] = [];
        if (Array.isArray(item)) {
            for (const spe of item) {
                if (spe.spe_speres) out.push(...spe.spe_speres);
            }
        }
        return out;
    };

    // Build a per-source map of sperse for the section at the same depth as
    // the click. Sections at one depth from both sources contribute their own
    // sperse to their own slot.
    const collectByDepthAndSource = (sections: any[], depthFrom: number, depthTo: number): Partial<Record<DataSource, any[]>> => {
        const eps = 0.001;
        const out: Partial<Record<DataSource, any[]>> = {};
        for (const sec of sections) {
            if (Math.abs(sec.startPos - depthFrom) < eps && Math.abs(sec.stopPos - depthTo) < eps) {
                const src = (sec.source ?? 'xrf') as DataSource;
                const arr = collectSperes(sec.item);
                if (arr.length > 0) out[src] = (out[src] ?? []).concat(arr);
            }
        }
        return out;
    };

    const handleSectionClick = useMemo(() => (item: any, worldPosition: THREE.Vector3, depthFrom: number, depthTo: number, index: number, sections: any[], basePos: [number, number, number], rotation: {azimuthRad: number; tiltRad: number} | null) => {
        setSelectedSection(item);
        setSelectedPosition(worldPosition);
        setSectionDepth({from: depthFrom, to: depthTo});
        setSelectedIndex(index);
        setCurrentSections(sections);
        setBasePosition(basePos);
        setSectionRotation(rotation);
        setShowPopup(true);
        const clickedSource = (sections[index]?.source ?? 'xrf') as DataSource;
        const bySource = collectByDepthAndSource(sections, depthFrom, depthTo);
        // Fallback so the popup still has the clicked source's data even if
        // the depth lookup missed (shouldn't normally happen).
        if (!bySource[clickedSource]) bySource[clickedSource] = collectSperes(item);
        setExplosionData(bySource[clickedSource] ?? []);
        setExplosionDataBySource(bySource);
        setExplosionSource(clickedSource);
    }, []);

    const handleCloseExplosion = () => {
        setExplosionData(null);
        setExplosionDataBySource(null);
        setSelectedPosition(null);
        setSectionDepth(null);
        setSelectedIndex(null);
        setCurrentSections(null);
        setBasePosition(null);
        setSectionRotation(null);
        setShowPopup(false);
    };

    // Use enriched selectedBoreholes from Redux, filtered by dropdown selection
    const boreholes = useMemo(() => {
        if (selectedDrillholeIds.length === 0) return [];
        const idSet = new Set(selectedDrillholeIds);
        return selectedBoreholes.filter((bh: any) => idSet.has(bh.hole_id));
    }, [selectedBoreholes, selectedDrillholeIds]);

    // Keyboard navigation for sections when popup is open
    useEffect(() => {
        if (!showPopup || !currentSections || selectedIndex == null || !basePosition) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault();
                e.stopPropagation();
                let newIdx = selectedIndex + (e.key === 'ArrowUp' ? -1 : 1);
                newIdx = Math.max(0, Math.min(currentSections.length - 1, newIdx));
                if (newIdx !== selectedIndex) {
                    const target = currentSections[newIdx];
                    const local = new THREE.Vector3(0, target.yPosition, 0);
                    if (sectionRotation) {
                        local.applyAxisAngle(new THREE.Vector3(1, 0, 0), sectionRotation.tiltRad);
                        local.applyAxisAngle(new THREE.Vector3(0, 1, 0), sectionRotation.azimuthRad);
                    }
                    const wp = new THREE.Vector3(basePosition[0] + local.x, basePosition[1] + local.y, basePosition[2] + local.z);
                    const clickedSource = (target.source ?? 'xrf') as DataSource;
                    const bySource = collectByDepthAndSource(currentSections, target.startPos, target.stopPos);
                    if (!bySource[clickedSource]) bySource[clickedSource] = collectSperes(target.item);
                    setSelectedIndex(newIdx);
                    setSelectedPosition(wp);
                    setSectionDepth({from: target.startPos, to: target.stopPos});
                    setExplosionData(bySource[clickedSource] ?? []);
                    setExplosionDataBySource(bySource);
                    setExplosionSource(clickedSource);
                }
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [showPopup, currentSections, selectedIndex, basePosition, sectionRotation]);

    const {boreholePositions, bounds, fitView} = useMemo(() => {
        const empty = {
            boreholePositions: [] as any[],
            bounds: {minX: 0, maxX: 0, minZ: 0, maxZ: 0},
            fitView: null as { position: [number, number, number], target: [number, number, number] } | null,
        };
        if (!boreholes || boreholes.length === 0) return empty;

        // Filter to boreholes with valid coordinates
        const withValidCoords = boreholes.filter((d: any) => d.latitude != null && d.longitude != null);
        if (withValidCoords.length === 0) return empty;

        // Use the geographic center of the selected boreholes as scene origin so
        // collar positions stay close to (0,0,0) even when far from the equator.
        const centerLat = withValidCoords.reduce((sum: number, d: any) => sum + d.latitude, 0) / withValidCoords.length;
        const centerLon = withValidCoords.reduce((sum: number, d: any) => sum + d.longitude, 0) / withValidCoords.length;

        // Borehole positions are in REAL METERS — no rescaling — so horizontal
        // spacing, depth and column thickness share the same scale as the SVG
        // chart at height factor 1.
        const positions = withValidCoords.map((drillhole: any) => {
            const [xKm, zKm] = latLonTo3D(drillhole.latitude, drillhole.longitude, centerLat, centerLon);
            const elevationOffset = (drillhole.elevation ?? 0) - nnOffset;
            return {
                drillhole,
                position: [xKm * 1000, elevationOffset, zKm * 1000] as [number, number, number]
            };
        });

        const bounds = positions.reduce(
            (acc: any, {position}: any) => ({
                minX: Math.min(acc.minX, position[0]),
                maxX: Math.max(acc.maxX, position[0]),
                minZ: Math.min(acc.minZ, position[2]),
                maxZ: Math.max(acc.maxZ, position[2])
            }),
            {minX: Infinity, maxX: -Infinity, minZ: Infinity, maxZ: -Infinity}
        );

        // Pick a camera distance that frames the full scene (collars + deepest
        // borehole bottom) with a small margin. Keeps the same view direction as
        // INITIAL_CAMERA_POSITION so the windrose / angle indicator stay sane.
        let topY = -Infinity;
        let bottomY = Infinity;
        for (const {drillhole, position} of positions) {
            const ownDepth = drillhole.dri_dripos?.reduce(
                (a: number, d: any) => ((d.depth ?? 0) > a ? (d.depth ?? 0) : a), 0,
            ) ?? 0;
            const top = position[1];
            const bot = position[1] - ownDepth;
            if (top > topY) topY = top;
            if (bot < bottomY) bottomY = bot;
        }
        const horizontalExtent = Math.max(bounds.maxX - bounds.minX, bounds.maxZ - bounds.minZ);
        const verticalExtent = topY - bottomY;
        const sceneSize = Math.max(horizontalExtent, verticalExtent, 40);
        const halfFov = (75 / 2) * Math.PI / 180;
        const fitDistance = (sceneSize / 2 / Math.tan(halfFov)) * 1.3;
        const dirLen = Math.sqrt(200 * 200 + 150 * 150 + 200 * 200);
        const dir: [number, number, number] = [200 / dirLen, 150 / dirLen, -200 / dirLen];
        const target: [number, number, number] = [
            (bounds.minX + bounds.maxX) / 2,
            (topY + bottomY) / 2,
            (bounds.minZ + bounds.maxZ) / 2,
        ];
        const fitView = {
            position: [
                target[0] + dir[0] * fitDistance,
                target[1] + dir[1] * fitDistance,
                target[2] + dir[2] * fitDistance,
            ] as [number, number, number],
            target,
        };

        return {boreholePositions: positions, bounds, fitView};
    }, [boreholes, nnOffset]);

    const scaleLabel3D = useMemo(() => {
        if (!cameraDistance) return undefined;
        return `1:${Math.round(cameraDistance)}`;
    }, [cameraDistance]);

    const depthsInfo3D = useMemo(() => {
        if (!boreholes || boreholes.length === 0) return [];
        return boreholes
            .filter((dh: any) => dh.dri_dripos && dh.dri_dripos.length > 0)
            .map((dh: any) => {
                const maxDepth = dh.dri_dripos.reduce((acc: any, d: any) =>
                    d.depth > acc ? d.depth : acc, 0);
                return {label: dh.hole_id ?? dh.id, from: 0, to: maxDepth};
            });
    }, [boreholes]);

    // Drillholes captured by the GeoMap line (within the buffer). Empty when no line is drawn.
    const drillholeOptions = useMemo(() => {
        if (!selectLine || !withCoords || withCoords.length === 0 || mapBufferOffset <= 0) return [];
        const c1 = [selectLine.x1, selectLine.y1];
        const c2 = [selectLine.x2, selectLine.y2];
        const ls = turf.lineString([c1, c2]);
        const buff = turf.buffer(ls, mapBufferOffset / 1.6, {units: 'meters'});
        const result: { value: string; label: string }[] = [];
        for (const item of withCoords) {
            if (!item.collar || item.collar.LONGITUDE == null || item.collar.LATITUDE == null) continue;
            const pt = turf.point([item.collar.LONGITUDE, item.collar.LATITUDE]);
            if (turf.booleanPointInPolygon(pt, buff)) {
                result.push({value: item.hole_id, label: item.hole_id});
            }
        }
        return result;
    }, [selectLine, withCoords, mapBufferOffset]);

    const hasBoreholes = boreholes && boreholes.length > 0 && elements;

    return (
        <div className='card w-full h-full'>
            <Dialog header="Save Template" visible={isAddTemplateDialogVisible} style={{width: '300px'}} onHide={() => setIsAddTemplateDialogVisible(false)}>
                <div className="flex flex-column gap-2">
                    <label htmlFor="templateName">Name</label>
                    <Dropdown id="templateName"
                                  value={newTemplateName}
                                  options={templates}
                                  optionLabel="name"
                                  optionValue="name"
                                  onChange={(e) => setNewTemplateName(e.value)}
                                  editable
                                  placeholder="Enter or select a name"
                                  className="p-inputtext-sm"
                                  autoFocus
                    />
                </div>
                <div className="flex justify-content-end mt-4">
                    <Button label="Cancel" icon="pi pi-times" onClick={() => setIsAddTemplateDialogVisible(false)} className="p-button-text p-button-secondary mr-2" />
                    <Button label="Save" icon="pi pi-check" onClick={handleSaveNewTemplate} disabled={!newTemplateName.trim()} />
                </div>
            </Dialog>
            <div style={{display: 'flex', height: '100%'}}>
                <div style={{flexShrink: 0, width: isControlBarCollapsed ? '36px' : '320px', overflow: isControlBarCollapsed ? 'hidden' : 'auto', transition: 'width 0.2s'}}>
                    <ControlBar selectedDrillhole={selectedSection} display={[]} setDisplay={() => {
                    }} showLegend={true} legendShowAmountColortable={false} legendClickable={true} legendMultiSelect={true} scale={scaleLabel3D} showDepths={false}
                    dataSources={dataSources}
                    onDataSourcesChange={(next) => dispatch(setBoreholes3DDataSources(next))}
                    activeOverrides={activeOverrides}
                    onActiveOverridesChange={(next) => dispatch(setBoreholes3DActiveOverrides(next))}
                    drillholeOptions={drillholeOptions}
                    selectedDrillholeIds={selectedDrillholeIds}
                    onDrillholeIdsChange={setSelectedDrillholeIds}
                    templates={templates}
                    selectedTemplateId={selectedTemplateId}
                    onApplyTemplate={handleApplyTemplate}
                    onSaveTemplate={() => {
                        const selected = templates.find(t => t.id === selectedTemplateId);
                        setNewTemplateName(selected ? selected.name : '');
                        setIsAddTemplateDialogVisible(true);
                    }}
                    onDeleteTemplate={handleDeleteTemplate}
                    legendHideInactive={legendHideInactive}
                    onLegendHideInactiveChange={setLegendHideInactive}
                    showMenuCard={true}
                    menuCardContent={<div className={'menu-button w-full'}>
                        <h4>Menu</h4>
                        <Input edit={true} label='Sea Level Offset (m)' name={'nnOffset'} type={'number'}
                               value={nnOffset}
                               onChange={(e: any) => {
                                   const v = typeof e === 'number' ? e : Number(e);
                                   setNnOffset(isFinite(v) ? v : 0);
                               }}/>
                        <Button label="Reset view" icon="pi pi-refresh"
                                className="p-button-outlined p-button-sm w-full mt-2"
                                onClick={() => setResetView(prev => !prev)}/>
                        <Input edit={true} name={'showDepthAxis'}
                               type={'checkbox'}
                               label={'Show Depth Scale'}
                               value={showDepthAxis}
                               onChange={(e: any) => {
                                   setShowDepthAxis(e.checked ?? e);
                               }}/>
                        <Input edit={true} name={'showGrid'}
                               type={'checkbox'}
                               label={'Gutter'}
                               value={showGrid}
                               onChange={(e: any) => {
                                   setShowGrid(e.checked ?? e);
                               }}/>
                        <Input edit={true} name={'useBoreholeAngle'}
                               type={'checkbox'}
                               label={'Show Borehole Angle'}
                               value={useBoreholeAngle}
                               onChange={(e: any) => {
                                   setUseBoreholeAngle(e.checked ?? e);
                               }}/>
                    </div>}
                    />
                </div>
                <div style={{flex: '1 1 0', overflow: 'hidden', minWidth: 0}}>
                    {(listLoading || detailsLoading) ? (
                        <div style={{width: '100%', height: '85vh', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                            <ProgressSpinner />
                        </div>
                    ) : !hasBoreholes ? (
                        <div style={{width: '100%', height: '85vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '16px'}}>
                            Please select boreholes
                        </div>
                    ) : (
                    <div
                                ref={containerRef}
                                style={{width: '100%', height: '85vh', position: 'relative'}}
                                onMouseMove={(e) => {
                                    if (containerRef.current) {
                                        const rect = containerRef.current.getBoundingClientRect();
                                        setMousePos({x: e.clientX - rect.left, y: e.clientY - rect.top});
                                    }
                                }}
                            >
                                <img className={'windrose'} src={'/windrose.png'} alt="Windrose"
                                     style={{transform: 'rotate(' + windroseRotation + 'deg)'}}/>
                                <ViewAngleIndicator polarAngle={polarAngle}/>
                                {/*HTML Popup*/}
                                {showPopup && explosionData && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '10px',
                                        left: '10px',
                                        zIndex: 100000000,
                                        width: '280px',
                                        background: 'white',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                                        overflow: 'hidden',
                                    }}>
                                        {/* Header with depth */}
                                        <div style={{
                                            background: 'linear-gradient(135deg, #3b3b5c, #5a5a8a)',
                                            color: 'white',
                                            padding: '12px 16px',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}>
                                            <div>
                                                <div style={{fontSize: '11px', opacity: 0.8, marginBottom: '2px'}}>Depth</div>
                                                <div style={{fontSize: '16px', fontWeight: 'bold'}}>
                                                    {sectionDepth
                                                        ? `${sectionDepth.from?.toFixed(2).replace('.', ',') ?? '0,00'} – ${sectionDepth.to?.toFixed(2).replace('.', ',') ?? '0,00'} m`
                                                        : '–'}
                                                </div>
                                            </div>
                                            <button
                                                onClick={handleCloseExplosion}
                                                style={{
                                                    background: 'rgba(255,255,255,0.2)',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '50%',
                                                    width: '24px',
                                                    height: '24px',
                                                    cursor: 'pointer',
                                                    fontSize: '14px',
                                                    lineHeight: '24px',
                                                    padding: 0,
                                                }}
                                            >×</button>
                                        </div>

                                        {/* Mineral list */}
                                        <div style={{padding: '8px 0', maxHeight: '300px', overflowY: 'auto'}}>
                                            {(() => {
                                                const sourceElements = elements.filter((e: any) => ((e as any).source ?? 'xrf') === explosionSource);
                                                const activeElementIds = new Set(sourceElements.filter((e: any) => e.active).map((e: any) => e.id));
                                                const mineralData = explosionData
                                                    .filter((s: any) => s.value > 0 && activeElementIds.has(s.element_id))
                                                    .sort((a: any, b: any) => b.value - a.value);
                                                const maxVal = mineralData.length > 0 ? mineralData[0].value : 1;

                                                if (mineralData.length === 0) {
                                                    return <div style={{padding: '12px 16px', color: '#999', fontSize: '13px'}}>No active minerals at this position</div>;
                                                }

                                                return mineralData.map((s: any, i: number) => {
                                                    const element = sourceElements.find((e: any) => e.id === s.element_id);
                                                    const barWidth = (s.value / maxVal) * 100;
                                                    return (
                                                        <div key={i} style={{
                                                            padding: '6px 16px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '10px',
                                                        }}>
                                                            <div style={{
                                                                width: '12px',
                                                                height: '12px',
                                                                borderRadius: '3px',
                                                                background: element?.color || '#999',
                                                                flexShrink: 0,
                                                            }}/>
                                                            <div style={{flex: 1, minWidth: 0}}>
                                                                <div style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    fontSize: '12px',
                                                                    marginBottom: '3px',
                                                                }}>
                                                                    <span style={{fontWeight: 600}}>{element?.name || `#${s.element_id}`}</span>
                                                                    <span style={{color: '#666'}}>{(s.value?.toFixed(2).replace('.', ',') ?? '0,00') + unitSuffix(element?.unit)}</span>
                                                                </div>
                                                                <div style={{
                                                                    height: '4px',
                                                                    borderRadius: '2px',
                                                                    background: '#eee',
                                                                    overflow: 'hidden',
                                                                }}>
                                                                    <div style={{
                                                                        height: '100%',
                                                                        width: `${barWidth}%`,
                                                                        borderRadius: '2px',
                                                                        background: element?.color || '#999',
                                                                    }}/>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                });
                                            })()}
                                        </div>
                                    </div>
                                )}

                                <Canvas
                                    camera={{position: INITIAL_CAMERA_POSITION, fov: 75}}
                                    style={{background: 'transparent'}}
                                >
                                    {/* Lighting */}
                                    <ambientLight intensity={1}/>
                                    <directionalLight position={[10, 10, 5]} intensity={1}/>

                                    {/* Scene background */}
                                    {/*<SceneBackground/>*/}

                                    {/* OSM map underlay */}
                                    {showGrid && <SceneGrid bounds={bounds} />}

                                    {/* Render boreholes */}
                                    {boreholePositions.map((item: any, index: number) => (
                                        <BoreholeColumn
                                            key={index}
                                            drillhole={item.drillhole}
                                            elements={elements}
                                            activeElement={activeElement}
                                            position={item.position}
                                            cameraDistance={throttledCameraDistance}
                                            onSectionClick={handleSectionClick}
                                        cameraMovedRef={cameraMovedRef}
                                        showDepthAxis={showDepthAxis}
                                        dataSources={dataSources}
                                        useAngle={useBoreholeAngle}
                                        boreholeCount={boreholePositions.length}
                                        />
                                    ))}

                                    {/* Explosion view */}
                                    {explosionDataBySource && selectedPosition && (
                                        <ExplosionView
                                            position={selectedPosition}
                                            speSperesBySource={explosionDataBySource}
                                            elements={elements}
                                            dataSources={dataSources}
                                            onClose={handleCloseExplosion}
                                            onHoverElement={setTooltip}
                                            rotation={sectionRotation}
                                        />
                                    )}

                                    {/* Camera animation to explosion */}
                                    <CameraAnimator
                                        target={explosionData ? selectedPosition : null}
                                        controlsRef={controlsRef}
                                    />

                                    {/* Camera zoom tracking */}
                                    <CameraZoomTracker
                                        onDistanceChange={setCameraDistance}
                                        onCameraChange={setCurrentCamera}
                                        onAnglesChange={(azimuth, polar) => {
                                            setWindroseRotation(azimuth * (180 / Math.PI) + 180);
                                            setPolarAngle(polar);
                                        }}
                                        cameraState={restoringTemplateCamera}
                                        resetView={resetView}
                                        fitView={fitView}
                                    />

                                    {/* Camera controls */}
                                    <OrbitControls
                                        ref={controlsRef}
                                        makeDefault
                                        enableDamping={false}
                                        enablePan={true}
                                        enableZoom={true}
                                        enableRotate={true}
                                        maxPolarAngle={Math.PI * 0.9}
                                        minDistance={5}
                                        maxDistance={20000}
                                        onStart={() => { cameraMovedRef.current = false; }}
                                        onChange={() => { cameraMovedRef.current = true; }}
                                        onEnd={() => { setTimeout(() => { cameraMovedRef.current = false; }, 0); }}
                                    />
                                </Canvas>

                                {/* Tooltip following mouse */}
                                {tooltip && (
                                    <div style={{
                                        position: 'absolute',
                                        left: mousePos.x + 12,
                                        top: mousePos.y - 10,
                                        background: 'rgba(255,255,255,0.95)',
                                        padding: '4px 10px',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        whiteSpace: 'nowrap',
                                        color: tooltip.element?.color || '#333',
                                        textShadow: '1px 1px 2px rgba(0,0,0,0.6)',
                                        border: `2px solid ${tooltip.element?.color || '#ccc'}`,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                        pointerEvents: 'none',
                                        zIndex: 1001,
                                    }}>
                                        {tooltip.element?.name || `#${tooltip.element_id}`}
                                        <span style={{fontWeight: 'normal', marginLeft: '6px'}}>
                                            {(tooltip.value?.toFixed(1) ?? '0.0') + unitSuffix(tooltip.element?.unit)}
                                        </span>
                                    </div>
                                )}
                            </div>
                    )}
                </div>
            </div>
        </div>
    );
}
